import { ChevronLeft, ChevronRight, CircleHelp, LockKeyhole, LockKeyholeOpen, RefreshCw } from 'lucide-react'

import { Button, ChoiceChips, SelectField, StatusMessage, Workbench } from '@krp/ui-kit'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import '../../../shared/ui/onec.css'
import { CashReceiptsPlacePicker } from '../../receipts/ui/CashReceiptsPlacePicker'
import '../../receipts/ui/receipts.css'
import type { CashShiftRowVm, CashShiftStateFilter, CashShiftsScreenActions, CashShiftsScreenVm } from '../model/types'
import './shifts.css'

/** Состояние смены — значком и словом, а не только цветом. */
function StateBadge({ row }: { row: CashShiftRowVm }) {
  const icon =
    row.state === 'open' ? <LockKeyholeOpen size={16} aria-hidden="true" /> : row.state === 'closed' ? <LockKeyhole size={16} aria-hidden="true" /> : <CircleHelp size={16} aria-hidden="true" />
  return (
    <span className="onec-shifts__state" data-state={row.state ?? 'unknown'}>
      {icon}
      {row.stateLabel}
    </span>
  )
}

/**
 * «Смены 1С»: кассовые смены базы, как их ведёт 1С (документы «Кассовая смена»). Слева — «Где смотрим» (организация → магазин →
 * касса), справа — месяц стрелками и дни месяца с числом смен, отбор кассира и состояния, ниже — смены. Только чтение: смену
 * открывает и закрывает касса. Чек смены отсюда не открывается — это раздел «Чеки». 1С не ответила — её текст и «Повторить».
 */
export function CashShiftsScreen({ vm, actions }: { vm: CashShiftsScreenVm; actions: CashShiftsScreenActions }) {
  const nav = vm.nav
  const busy = vm.query.refreshing || vm.query.status === 'loading'
  return (
    <section className="onec-screen" aria-label="Кассовые смены базы">
      {nav.note !== null && <p className="onec-field__note">{nav.note}</p>}
      <Workbench
        sideTitle="Где смотрим"
        sideSummary={nav.summaryText}
        side={
          nav.query.status === 'ready' ? (
            <CashReceiptsPlacePicker nav={nav} onSelect={actions.nodeChosen} onToggle={actions.nodeToggled} />
          ) : (
            <OneCQueryState query={nav.query} emptyText="В базе нет касс ККМ" onRetry={actions.retry} />
          )
        }
      >
        <div className="onec-toolbar">
          <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} busy={busy} onClick={actions.refresh}
            title="Перечитать кассы и смены месяца из 1С">
            Обновить
          </Button>
          {vm.readAt !== null && <span className="onec-muted">{vm.readAt}</span>}
        </div>
        <div className="krp-days krp-days--pager" role="group" aria-label="Месяц и день">
          <div className="krp-days__pager">
            <button type="button" className="krp-days__arrow" aria-label="Предыдущий месяц" title="Показать смены предыдущего месяца"
              onClick={() => actions.monthChanged(vm.previousMonth)}>
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <strong className="krp-days__pager-title" aria-live="polite">{vm.monthTitle}</strong>
            <button type="button" className="krp-days__arrow" aria-label="Следующий месяц" title={vm.canNext ? 'Показать смены следующего месяца' : 'Следующий месяц ещё не наступил'}
              disabled={!vm.canNext} onClick={() => actions.monthChanged(vm.nextMonth)}>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="krp-days__row" role="group" aria-label={`Дни: ${vm.monthTitle}`}>
            <button type="button" className="krp-day krp-day--wide" aria-pressed={vm.day === null} title="Смены всего месяца" onClick={() => actions.dayChanged(null)}>
              Весь месяц
            </button>
            {vm.days.map((day) => (
              <button
                key={day.day}
                type="button"
                className="krp-day onec-shifts__day"
                aria-pressed={day.selected}
                aria-label={day.label}
                title={day.future ? 'Этот день ещё не наступил' : `${day.label}. Нажмите ещё раз — весь месяц`}
                disabled={day.future}
                data-today={day.today || undefined}
                data-has-shifts={day.count > 0 || undefined}
                onClick={() => actions.dayChanged(day.selected ? null : day.day)}
              >
                <span className="krp-day__weekday">{day.weekday}</span>
                <span className="krp-day__date">{day.date}</span>
                <span className="onec-shifts__day-count">{day.count > 0 ? `${day.count} см.` : '—'}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="onec-shifts__filters">
          <ChoiceChips label="Состояние смены" items={vm.states} value={vm.state} onChange={(state) => actions.stateChanged(state as CashShiftStateFilter)} />
          <SelectField label="Кассир" value={vm.cashier} options={vm.cashiers} onChange={actions.cashierChanged} />
        </div>
        {vm.truncatedNote !== null && <StatusMessage kind="empty" title="Показаны не все смены" text={vm.truncatedNote} />}
        <OneCQueryState query={vm.query} emptyText="За месяц смен в 1С нет" onRetry={actions.retry} />
        {vm.query.status === 'ready' && <p className="onec-shifts__count" aria-live="polite">{vm.countText}</p>}
        {vm.filteredOut && (
          <StatusMessage kind="empty" title="По этому отбору смен нет" text="Число справа от места в дереве — сколько у него смен. Смените место, день, кассира или состояние." />
        )}
        {vm.rows.length > 0 && (
          <div className="krp-receipt-table-wrap">
            <table className="krp-receipt-table" aria-label="Кассовые смены">
              <thead>
                <tr><th>Смена</th><th>Касса</th><th>Кассир</th><th>Открыта</th><th>Закрыта</th><th>Состояние</th></tr>
              </thead>
              <tbody>
                {vm.rows.map((row) => (
                  <tr key={row.id} data-state={row.state ?? 'unknown'}>
                    <td>{row.title}{row.note !== null && <small className="onec-shifts__note">{row.note}</small>}</td>
                    <td>{row.register}</td>
                    <td>{row.cashier}</td>
                    <td>{row.opened}</td>
                    <td>{row.closed}</td>
                    <td><StateBadge row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Workbench>
    </section>
  )
}
